import { ClientRequestModel, EngagementStepType } from '@aiwyn/api';
import { AuthService, SecureLinkEntityType } from '@aiwyn/auth';
import { GetSecondaryData } from '@aiwyn/client-requests';
import {
  ClientRequestIdentityHelper,
  CommonClientRequestFactoryService,
  GetAllClientRequestGroups,
  GetClientRequests,
} from '@aiwyn/common/client-requests';
import { ENGAGEMENT_STEP_LABEL_MAP, EngagementsState, GetEngagementHub, STEP_LABEL_DOCUMENT_TYPE_MAPPING } from '@aiwyn/engagements';
import { LoadableArrayLoadStrategy, LoadableHelper, LoadableStatus, Logger } from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { GetUserContactIds, SetLoggedInUserContactId } from '../../../state';
import { ClientRequestWrapperComponent, EngagementWrapperComponent } from '../components';
import {
  ClientRequestEntityRequest,
  EngagementStepEntityRequest,
  EntityRequest,
  GrantEntity,
  SecureLinkClientRequest,
  SecureLinkEngagementStep,
  SecureLinkEntityIdentity,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class SecureLinkEntityFactoryService {

  constructor(
    private readonly logger: Logger,
    private readonly store: Store,
    private readonly clientRequestFactoryService: CommonClientRequestFactoryService,
    private readonly authService: AuthService,
  ) { }

  getSecureLinkEntitiesByType<EntityType extends SecureLinkEntityType>(
    request: EntityRequest<EntityType>,
  ): Observable<GrantEntity<EntityType>[]> {
    if (!request.entityType || !request.entityIds) {
      this.logger.error(
        'SecureLinkEntityFactoryService :: getSecureLinkEntity :: Invalid entityType or entityId provided to getSecureLinkEntity');
      return EMPTY;
    }

    if (request.entityType === SecureLinkEntityType.CLIENT_REQUEST) {
      return this.getClientRequestEntity(request as ClientRequestEntityRequest) as unknown as Observable<GrantEntity<EntityType>[]>;
    }

    if (request.entityType === SecureLinkEntityType.ENGAGEMENT_STEP) {
      return this.getEngagementEntity(request as EngagementStepEntityRequest) as unknown as Observable<GrantEntity<EntityType>[]>;
    }

    this.logger.warn(`SecureLinkEntityFactoryService :: getSecureLinkEntity :: Unhandled entityType: ${request.entityType} in getSecureLinkEntity`);
    return of([]) as Observable<GrantEntity<EntityType>[]>;
  }

  private getClientRequestEntity(
    { entityIds, restrictedRequestItemsByRequestId }: ClientRequestEntityRequest,
  ): Observable<GrantEntity<SecureLinkEntityType.CLIENT_REQUEST>[]> {
    return this.store.dispatch([
      new GetAllClientRequestGroups(),
      new GetClientRequests(
        entityIds,
        LoadableArrayLoadStrategy.LOAD,
        [...(restrictedRequestItemsByRequestId?.values() || [])].reduce((acc, itemIds) => [...acc, ...itemIds], []),
      ),
    ]).pipe(
      switchMap(_ => this.store.dispatch(new GetSecondaryData())),
      map(_ => entityIds.map(entityId => this.clientRequestFactoryService.getClientRequest(
        ClientRequestIdentityHelper.getRequestIdentity({ id: entityId } as ClientRequestModel),
      ))),
      switchMap(clientRequests => combineLatest(
        clientRequests.map(request => request.model$.pipe(
          filter(({ status }) => LoadableHelper.isLoaded(status, true)),
          map(({ status, data }) => {
            if (status === LoadableStatus.ERROR) {
              throw new Error('Failed to load client request');
            }

            return data;
          }),
          // Map to an appropriate status for secure link nav
          switchMap(clientRequest => request.status$.pipe(
            map(status => ClientRequestWrapperComponent.normalizeStatus(status)),
            map(entityStatus => {
              const grantEntity: Omit<GrantEntity<SecureLinkEntityType.CLIENT_REQUEST>, 'entityIdentity'> = {
                jobId: clientRequest.jobId,
                clientId: clientRequest.clientId,

                entityId: clientRequest.id,
                entityType: SecureLinkEntityType.CLIENT_REQUEST as const, // without the `as const` it widens to SecureLinkEntityType, causing type issues
                entityName: clientRequest.name,
                entityStatus,
                entity: {
                  request,
                  clientRequestItemIds: restrictedRequestItemsByRequestId.get(clientRequest.id),
                } as SecureLinkClientRequest,
              };

              return {
                ...grantEntity,
                entityIdentity: ClientRequestWrapperComponent.getEntityIdentity(grantEntity.entity),
              };
            }),
          )),
        ))),
      ),
      take(1),
    );
  }

  private getEngagementEntity(
    { entityIds, engagementMap }: EngagementStepEntityRequest,
  ): Observable<GrantEntity<SecureLinkEntityType.ENGAGEMENT_STEP>[]> {
    return this.authService.getUser().pipe(
      take(1),
      switchMap(user => {
        // TODO: Remove this once we have a proper way to set the logged in user contact id
        const currentUserContactId = user.getContactId();
        this.store.dispatch(new SetLoggedInUserContactId(currentUserContactId));

        return this.store.dispatch(new GetUserContactIds(user.getProviderEmail(), user.getContactId()))
      }),
      switchMap(_ => this.store.dispatch(new GetEngagementHub(entityIds[0], true))),
      map(() => {
        const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
        const allowedDocumentContactIds = engagementMap?.get(selectedEngagement.id) ?? [];

        const relevantEngagementSteps = selectedEngagement.report.steps.reduce((acc, step) => {
          const stepDocumentId = step.documentId ?? step.tasks?.find(task => task.documentId)?.documentId;

          const documentInfoFromId = stepDocumentId != null
            ? selectedEngagement.documentsInfoById?.[stepDocumentId]
            : undefined;

          const fallbackDocumentType = step.type !== EngagementStepType.PAYMENT
            ? STEP_LABEL_DOCUMENT_TYPE_MAPPING[step.type as Exclude<EngagementStepType, EngagementStepType.PAYMENT>]
            : undefined;

          const fallbackDocumentInfo = fallbackDocumentType != null
            ? selectedEngagement.documentsInfo?.[fallbackDocumentType]
            : undefined;

          const documentContactId = documentInfoFromId?.documentContactId ?? fallbackDocumentInfo?.documentContactId;

          if (!documentContactId || !allowedDocumentContactIds.includes(documentContactId)) {
            return acc;
          }

          const resolvedDocumentId = stepDocumentId ?? step.documentId;

          const secureLinkStep: SecureLinkEngagementStep = {
            ...step,
            documentId: resolvedDocumentId,
            engagementId: selectedEngagement.id,
            documentContactId,
          };

          return acc.concat(secureLinkStep);
        }, new Array<SecureLinkEngagementStep>());

        // Preserve the step order from the API response (report.steps), which represents the
        // business-configured order. The engagement hub landing page also uses this order via
        // getSelectedEngagementVisibleStepsDisplay, so the side drawer must match.
        return relevantEngagementSteps.map(step => ({
          entityId: step.documentContactId, // I know this looks weird, but assume a document contact is 1:1 with an engagement step
          entityIdentity: EngagementWrapperComponent.getEntityIdentity(step),
          entityType: SecureLinkEntityType.ENGAGEMENT_STEP as const, // without the `as const` it widens to SecureLinkEntityType, causing type issues
          entityName: (() => {
            const labelValue = ENGAGEMENT_STEP_LABEL_MAP[step.type];
            return typeof labelValue === 'function' ? labelValue(step.label) : labelValue;
          })(),
          entityStatus: EngagementWrapperComponent.normalizeStatus(step.status),
          entity: step,
          clientId: selectedEngagement.clientId,
        }));
      }),
    );
  }

  static getEntityIdentity(entity: GrantEntity): SecureLinkEntityIdentity {
    switch (entity.entityType) {
      case SecureLinkEntityType.CLIENT_REQUEST:
        return ClientRequestWrapperComponent.getEntityIdentity(entity.entity as SecureLinkClientRequest);
      case SecureLinkEntityType.ENGAGEMENT_STEP:
        return EngagementWrapperComponent.getEntityIdentity(entity.entity as SecureLinkEngagementStep);
      default:
        throw new Error(`SecureLinkContainerComponent :: getEntityIdentity :: Unhandled entity type ${entity.entityType}`);
    }
  }
}
